/**
 * WiFi Overrides Service
 * Supports both HTTP (real backend) and Mock implementations via feature flag
 * Feature flag: process.env.VUE_APP_WIFI_OVERRIDES_USE_MOCK
 */


import axiosHelper from '../utils/axios';
import WifiOverride from '../models/wifiOverride';

 const networkAssetPath = '/networkAsset';

// ============================================================================
// UTILITY: BSSID Canonicalization
// ============================================================================
export function canonicalizeBssid(input) {
  if (input == null) return '';
  const hex = String(input)
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-F]/g, '');
  if (hex.length !== 12) return '';
  return hex;
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================
function isValidLat(lat) {
  return typeof lat === 'number' && !Number.isNaN(lat) && lat >= -90 && lat <= 90;
}

function isValidLng(lng) {
  return typeof lng === 'number' && !Number.isNaN(lng) && lng >= -180 && lng <= 180;
}


// ============================================================================
// HTTP IMPLEMENTATION (Real Backend)
// ============================================================================

const httpService = {
  async list({ orgId, page = 1, size = 25, bssid = '', fetchAll } = {}) {
    if (!orgId) throw new Error('Organization ID required');
    
    const useFetchAll = (fetchAll === true);
    const effectivePage = useFetchAll ? 1 : (page || 1);
    const effectiveSize = useFetchAll ? 10000 : (size || 25);

    const params = {
      orgId: orgId,
      page: effectivePage,
      size: effectiveSize,
    };
    if (bssid) params.bssid = bssid;

    try {
      const response = await axiosHelper
        .getNetworkAsset()
        .get(`${networkAssetPath}/configurations/wifi_override/list`, { params });
      
      const items = Array.isArray(response.data) ? response.data : [];
      const model = new WifiOverride();
      const headers = response.headers || {};
      const totalFromHeaders = 
        parseInt(headers['total_filtered_records']) ||
        parseInt(headers['total_site_records']) ||
        items.length;
      
      return {
        data: items.map(item => model.normalize(item)),
        total: totalFromHeaders,
        page: effectivePage,
        size: effectiveSize,
      };
    } catch (error) {
      throw handleHttpError(error);
    }
  },

  async upsert({ orgId, bssid, lat, lng }) {
    if (!orgId) throw new Error('Organization ID required');
    
    const canonical = canonicalizeBssid(bssid);
    if (!canonical) throw new Error('Invalid BSSID format');
    if (!isValidLat(lat)) throw new Error('Invalid latitude');
    if (!isValidLng(lng)) throw new Error('Invalid longitude');

    try {
      const response = await axiosHelper
        .getNetworkAsset()
        .post(`${networkAssetPath}/configurations/wifi_override`, {
          bssid: canonical,
          latitude: String(lat),    
          longitude: String(lng),   
          orgId: orgId,             
        });
      return response.data;
    } catch (error) {
      throw handleHttpError(error);
    }
  },

  async update({ orgId, bssid, lat, lng }) {
    const canonical = canonicalizeBssid(bssid);
    if (!canonical) throw new Error('Invalid BSSID format');
    if (!isValidLat(lat)) throw new Error('Invalid latitude');
    if (!isValidLng(lng)) throw new Error('Invalid longitude');

    try {
      const response = await axiosHelper
        .getNetworkAsset()
        .put(
          `${networkAssetPath}/configurations/wifi_override/${canonical}`,
          null,
          {
            params: {
              bssid: canonical,
              latitude: String(lat),   
              longitude: String(lng),   
              orgId: orgId,             
            },
          }
        );
      return response.data;
    } catch (error) {
      throw handleHttpError(error);
    }
  },

  async create({ orgId, bssid, lat, lng }) {
    return this.upsert({ orgId, bssid, lat, lng });
  },

  async remove({ orgId, bssid }) {
    const canonical = canonicalizeBssid(bssid);
    if (!canonical) throw new Error('Invalid BSSID format');

    try {
      await axiosHelper
        .getNetworkAsset()
        .delete(`${networkAssetPath}/configurations/wifi_override`, {
          data: { bssid: canonical, orgId: orgId }    
        });
      return { ok: true };
    } catch (error) {
      throw handleHttpError(error);
    }
  },

  async bulkUpload({ orgId, file, dryRun = true }) {
    if (!orgId) throw new Error('Organization ID required');
    if (!file) throw new Error('CSV file is required');

    try {
      const form = new FormData();
      form.append('file', file, file.name || 'wifi_overrides.csv');
      form.append('dry_run', String(dryRun));
      form.append('orgid', orgId);

      const response = await axiosHelper
        .getNetworkAsset()
        .post(
          `${networkAssetPath}/configurations/wifi_override/bulk`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      return response.data;
    } catch (error) {
      throw handleHttpError(error);
    }
  },
};

function handleHttpError(error) {
  console.log(' [handleHttpError] Full error:', error);
  console.log(' [handleHttpError] error.response:', error.response);
  console.log(' [handleHttpError] error.status:', error.status);
  console.log(' [handleHttpError] error.data:', error.data);
  
  const status = error.response?.status || error.status;
  const data = error.response?.data || error.data;
  
  if (status && data) {
    console.log(' [handleHttpError] Status:', status);
    console.log(' [handleHttpError] Data:', data);
    
    switch (status) {
      case 400:
        return new Error(data.message || 'Invalid request parameters');
      case 403:
        return new Error('Organization not authorized');
      case 404:
        return new Error('Resource not found');
      case 409:
        console.log(' [handleHttpError] 409 detected, data.description:', data.description);
        if (data.description === 'DuplicateKeyException' || 
            (data.message && data.message.includes('Duplicate entry'))) {
          console.log(' [handleHttpError] Returning BSSID duplicate message');
          return new Error('BSSID already exists. Use Edit to update the existing override.');
        }
        console.log(' [handleHttpError] Returning generic conflict message');
        return new Error(data.message || 'Conflict: Resource already exists');
      default:
        return new Error(data.message || 'Server error');
    }
  }
  console.log(' [handleHttpError] No status/data, returning Network error');
  return new Error(error.message || 'Network error');
}

export default httpService;